import { Navigate, Outlet, useLocation } from 'react-router'
import { BootIndicator } from '../platform/async/BootIndicator'
import { SessionUnavailable } from '../routes/auth/SessionUnavailable'
import { getSessionRevoked } from '../api/sessionRevokedStore'
import { forgetReturnTo, rememberReturnTo, useReturnTo } from './returnTo'
import { useAuth } from './useAuth'

/**
 * Where a raised second factor is answered.
 *
 * Exported so `App.tsx` and both guards name the same string. Two of the three
 * used to hold their own literal, and the third is a route definition — a
 * mismatch would not fail to compile, it would strand the user on a screen that
 * cannot help them.
 */
export const MFA_PATH = '/mfa'

/**
 * The gate on every chrome route.
 *
 * `unknown` and `bootstrapping` do not REDIRECT. They are the states before
 * there is an answer, and treating either as signed-out bounces every
 * returning user to /signin for a frame — a visible flash, and a destination
 * lost for anyone who arrived by deep link.
 *
 * They used to render nothing at all, which was right about the navigation and
 * wrong about the screen: the app opened on a blank page for the length of a
 * Firebase resolution plus a session POST, with no way to tell a slow boot
 * from a broken one. #262 puts the design system's honeycomb there — this is
 * the "App shell boot" its own scope line names, and the only place in the app
 * that is a full-page wait rather than a surface inside a shell.
 *
 * The indicator is deliberately UNCONDITIONAL, with no minimum display time.
 * The 200ms floor belongs to all six indicators and is #254's to place; a
 * local one here would be the second implementation of a policy that has no
 * first. See BootIndicator's header.
 *
 * `sessionUnavailable` renders in place rather than redirecting, because there
 * is no route that would help: the user is authenticated, and every screen
 * behind this gate would render its own error with no common cause. One screen
 * naming the cause beats six naming symptoms. Rendering in place also keeps the
 * URL, so a retry that works lands them where they were going.
 */
type RequireAuthProps = {
  /**
   * Where a user who has not been through first run is sent.
   *
   * A PROP rather than a constant imported from `App.tsx`, and the reason is a
   * guard: `App.test.tsx` resolves every literal `path=` against a declaration
   * in that file, and deliberately FAILS on a constant it cannot resolve rather
   * than passing silently. Declaring the path here and importing it there would
   * trip that; declaring it in both places is the drift a constant exists to
   * prevent. Passing it in leaves one declaration, in the file that owns the
   * route table.
   */
  welcomePath: string
}

export function RequireAuth({ welcomePath }: RequireAuthProps) {
  const auth = useAuth()
  const location = useLocation()

  if (auth.status === 'unknown' || auth.status === 'bootstrapping') return <BootIndicator />

  if (auth.status === 'mfaRequired') return <Navigate to={MFA_PATH} replace />

  if (auth.status === 'sessionUnavailable') return <SessionUnavailable />

  if (auth.status === 'signedOut') {
    // Written before the redirect renders, so it survives whatever the sign-in
    // screen does next — including a full-document social redirect.
    //
    // `search` as well as `pathname`: `/transactions?page=2` is not the same
    // destination as `/transactions`, and the two halves arrive separately.
    //
    // Except after a server revocation: that is not the user walking away from
    // this page, the next sign-in goes to Overview, and anything stale goes too.
    if (getSessionRevoked() === null && auth.noReturnTo !== true) rememberReturnTo(`${location.pathname}${location.search}`)
    else forgetReturnTo()
    return <Navigate to="/signin" replace />
  }

  /**
   * First run, once and optionally.
   *
   * **After every other arm**, so it can only fire for a user who genuinely has
   * a session — `profile` does not exist before `signedIn`, and asking earlier
   * would be reading a field that is not there yet.
   *
   * **Explicitly `=== false`.** Not for the reason first written here — that
   * said "an older account whose record predates the field", which the schema
   * flatly contradicts: `is_onboarded BOOLEAN NOT NULL DEFAULT FALSE`, and the
   * generated type is required and non-nullable, so `undefined` does not occur
   * in practice. web-tester caught the wrong justification, and a wrong reason
   * is what gets a correct check deleted later.
   *
   * The real reason is narrower and still worth it: this is a redirect, and a
   * redirect that fires on ANY falsy value — including one a future shape
   * change introduces — sends users somewhere the only exit from is a write
   * they cannot reach. Being explicit costs nothing and bounds the blast
   * radius of a field that changes meaning.
   *
   * This route is NOT in the bare group, so nothing bounces it back; `Welcome`
   * itself leaves unconditionally whether its PATCH succeeded or not. Both
   * halves are tested, because the trap on `/mfa` was exactly this shape.
   */
  if (auth.profile.is_onboarded === false) {
    // Remembered, exactly as the signedOut arm does eleven lines up — and for
    // the same reason, including the query string. Without this a deep link
    // survived being signed out and was then thrown away by first run, which
    // is the one interruption guaranteed to happen on a user's FIRST visit.
    // It was invisible while first run was inescapable; fixing that made it
    // reachable. web-tester found it.
    rememberReturnTo(`${location.pathname}${location.search}`)
    return <Navigate to={welcomePath} replace />
  }

  return <Outlet />
}

/**
 * The other half of M1-R7, which the plan leaves out and its own test requires.
 *
 * A coarse-pointer user signs in with Google by REDIRECT (M1-R23), leaves the
 * origin, and comes back to `/signin` as a fresh document. The provider then
 * bootstraps them to `signedIn` — on the sign-in screen, with the form still in
 * front of them and nothing happening. `RequireAuth` cannot help: they are not
 * on a guarded route. So the pre-login routes need the mirror-image guard.
 *
 * The destination is read here rather than in the sign-in form because this is
 * the one place that knows the session has actually arrived — the form's own
 * `signIn()` resolving says only that Firebase accepted the credential.
 *
 * **`useReturnTo`, not `takeReturnTo`.** The read consumes, so calling it in
 * this render body made the render impure and `<StrictMode>`'s double invoke
 * threw away the destination it had just taken. See the hook's header.
 *
 * Only `signedIn` moves anyone, and only `signedIn` may CONSUME — which is why
 * the status is passed to the hook rather than the call being placed after the
 * early return. Taking the destination while the user is still looking at the
 * sign-in form empties storage into a ref that `signInWithRedirect` then throws
 * away with the document. `bootstrapping` is not yet a session either, and
 * redirecting there would cost a second navigation when the session POST fails.
 */
export function RedirectWhenSignedIn() {
  const auth = useAuth()
  const location = useLocation()
  const destination = useReturnTo(auth.status === 'signedIn')

  /**
   * A raised second factor, answered on a BARE route.
   *
   * Found by signing in locally with a real enrolled SMS factor: the challenge
   * was raised, the state went to `mfaRequired`, and the app sat on the sign-in
   * form with the button re-enabled and nothing said. `RequireAuth` sends
   * challenged users to `/mfa`, but it only guards CHROME routes — and a
   * challenge is raised on `/signin`, which is bare. So `/mfa` was unreachable
   * by the ordinary path, for every second factor including TOTP.
   *
   * This is the same shape as #180: a state the mirror guard did not name,
   * falling through to the sign-in form. The form cannot report it either —
   * `signIn()` does not resolve at all when a factor is raised, it throws
   * `MfaRequiredError`, which the provider swallows to dispatch the challenge.
   *
   * **The `/mfa` exemption is not cosmetic.** That route is inside this very
   * group, so without it the guard redirects the MFA screen to itself and the
   * outlet never renders — a blank screen instead of a stranded one.
   */
  if (auth.status === 'mfaRequired') {
    return location.pathname === MFA_PATH ? <Outlet /> : <Navigate to={MFA_PATH} replace />
  }

  /**
   * The same answer `RequireAuth` gives, because it is the same situation.
   *
   * #180, and it took a deploy to find. The user authenticated with Firebase
   * and the session POST failed, so the state is `sessionUnavailable` — and
   * without this they fall through to the sign-in FORM, with the button
   * re-enabled and nothing said. Pressing Sign in again repeats it, because the
   * credential was never the problem.
   *
   * The form cannot report it either: `auth.signIn()` RESOLVES, since Firebase
   * accepted the credential, so there is no throw for the screen to catch. The
   * failure arrives afterwards, in the provider — which is exactly why this
   * belongs to the guard and not to `SignIn`.
   *
   * In place rather than redirecting, for the reason `RequireAuth` already
   * gives: the URL is kept, so a Retry that works lands the user where they
   * were going.
   */
  if (auth.status === 'sessionUnavailable') return <SessionUnavailable />

  if (auth.status !== 'signedIn') return <Outlet />
  return <Navigate to={destination} replace />
}
